<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import KnowledgeSearchAPI from '../../../api/knowledgeSearch';

const { t } = useI18n();
const query = ref('');
const hits = ref([]);
const loading = ref(false);
const error = ref('');

const search = async () => {
  const value = query.value.trim();
  if (!value) {
    hits.value = [];
    error.value = '';
    return;
  }
  if (value.length > 200) {
    error.value = t('CONVERSATION.KNOWLEDGE_SEARCH_TOO_LONG');
    return;
  }

  loading.value = true;
  error.value = '';
  try {
    const response = await KnowledgeSearchAPI.search(value);
    hits.value = response.data.hits || [];
  } catch (e) {
    hits.value = [];
    error.value =
      e.response?.data?.error === 'knowledge_search_unavailable'
        ? t('CONVERSATION.KNOWLEDGE_SEARCH_UNAVAILABLE')
        : t('CONVERSATION.KNOWLEDGE_SEARCH_FAILED');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <section
    class="h-full overflow-y-auto p-4"
    :aria-label="$t('CONVERSATION.KNOWLEDGE_SEARCH_ARIA_LABEL')"
  >
    <form class="flex gap-2 mb-4" @submit.prevent="search">
      <input
        v-model="query"
        class="flex-1 min-w-0 rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-sm"
        type="search"
        maxlength="201"
        :placeholder="$t('CONVERSATION.KNOWLEDGE_SEARCH_PLACEHOLDER')"
        :aria-label="$t('CONVERSATION.KNOWLEDGE_SEARCH_PLACEHOLDER')"
      />
      <button
        class="rounded-md bg-n-brand px-4 py-2 text-sm text-white disabled:opacity-50"
        type="submit"
        :disabled="loading || !query.trim()"
      >
        {{
          loading
            ? $t('CONVERSATION.KNOWLEDGE_SEARCH_LOADING')
            : $t('CONVERSATION.KNOWLEDGE_SEARCH_ACTION')
        }}
      </button>
    </form>
    <p v-if="error" class="mb-3 text-sm text-n-ruby-12" role="alert">
      {{ error }}
    </p>
    <p
      v-else-if="!loading && !hits.length && query.trim()"
      class="text-sm text-n-slate-11"
    >
      {{ $t('CONVERSATION.KNOWLEDGE_SEARCH_EMPTY') }}
    </p>
    <ul v-else class="space-y-3">
      <li
        v-for="hit in hits"
        :key="hit.id"
        class="rounded-lg border border-n-weak p-3"
      >
        <div class="mb-1 flex items-start justify-between gap-3">
          <h3 class="font-medium text-n-slate-12">{{ hit.title }}</h3>
          <span class="shrink-0 text-xs text-n-slate-10">{{
            hit.score.toFixed(3)
          }}</span>
        </div>
        <p class="whitespace-pre-wrap text-sm text-n-slate-11">
          {{ hit.excerpt }}
        </p>
      </li>
    </ul>
    <p class="mt-4 text-xs text-n-slate-10">
      {{ $t('CONVERSATION.KNOWLEDGE_SEARCH_READ_ONLY') }}
    </p>
  </section>
</template>
